import styled, {keyframes} from 'styled-components';
import {ReactNode} from 'react';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const Wrapper = styled.div<{$compact: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$compact ? 10 : 14)}px;
  width: 100%;
  padding: ${(p) => (p.$compact ? '28px 16px' : '48px 24px')};
  text-align: center;
  animation: ${fadeInUp} ${(p) => p.theme.motion.duration.slow}
    ${(p) => p.theme.motion.easing.out} both;
`;

const IllustrationWrapper = styled.div<{$compact: boolean}>`
  position: relative;
  width: ${(p) => (p.$compact ? 64 : 92)}px;
  height: ${(p) => (p.$compact ? 64 : 92)}px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${(p) => p.theme.gradients.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: ${(p) => p.theme.elevation.md};
  animation: ${float} 4s ${(p) => p.theme.motion.easing.standard} infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -14px;
    border-radius: 50%;
    background: ${(p) => p.theme.gradients.glow};
    z-index: -1;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.primary500};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.2px;
`;

const Description = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 13px;
  line-height: 1.65;
  max-width: 380px;
`;

const ActionSlot = styled.div`
  margin-top: 6px;
`;

const DefaultIcon = ({size}: {size: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="mp-empty-gradient" x1="0" y1="0" x2="24" y2="24">
        <stop offset="0%" stopColor="#2F3071" />
        <stop offset="100%" stopColor="#7468D6" />
      </linearGradient>
    </defs>
    <rect
      x="3"
      y="4.5"
      width="18"
      height="15"
      rx="3.5"
      stroke="url(#mp-empty-gradient)"
      strokeWidth="1.6"
    />
    <path
      d="M7 10h10M7 14h6"
      stroke="url(#mp-empty-gradient)"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}

export const EmptyState = ({
  title,
  description,
  icon,
  action,
  compact = false,
  className,
}: EmptyStateProps) => (
  <Wrapper $compact={compact} className={className}>
    <IllustrationWrapper $compact={compact}>
      {icon ?? <DefaultIcon size={compact ? 26 : 34} />}
    </IllustrationWrapper>
    <Title>{title}</Title>
    {description ? <Description>{description}</Description> : null}
    {action ? <ActionSlot>{action}</ActionSlot> : null}
  </Wrapper>
);
